import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { vendorService } from '../../../services/api';
import DateRangePicker from '../../../components/DateRangePicker';
import { Eye, EyeOff, ChevronDown } from 'lucide-react';

const maskPhoneNumber = (phone) => {
  if (!phone || phone === 'N/A') return 'N/A';
  const cleanPhone = phone.trim();
  if (cleanPhone.length < 4) return cleanPhone;
  return '••••••' + cleanPhone.slice(-4);
};

const getPageNumbers = (currentPage, totalPages) => {
  const pages = [];
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    for (let i = 0; i < totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(0);

    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages - 2, currentPage + 1);

    if (currentPage <= 1) {
      end = 3;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 4;
    }

    if (start > 1) {
      pages.push('ellipsis1');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 2) {
      pages.push('ellipsis2');
    }

    pages.push(totalPages - 1);
  }
  return pages;
};

const IncompleteMembershipsTab = () => {
  const { hasPermission } = useAuth();

  // Filters State
  const [incompleteFilters, setIncompleteFilters] = useState({ startDate: '', endDate: '' });
  
  // Pagination State
  const [incompletePage, setIncompletePage] = useState(0);
  const [incompletePageSize, setIncompletePageSize] = useState(25);

  // Data State
  const [incompleteUpgrades, setIncompleteUpgrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // UI Interactive State
  const [revealedIncompletePhone, setRevealedIncompletePhone] = useState(null);

  const fetchIncompleteUpgrades = useCallback(async () => {
    if (!hasPermission('MANAGE_MEMBERSHIPS')) return;
    setLoading(true);
    setError('');
    try {
      const res = await vendorService.getAdminMemberships();
      if (res.success && res.data) {
        setIncompleteUpgrades(res.data.incomplete_upgrades || []);
      } else {
        setError(res.error || 'Failed to fetch incomplete payments.');
      }
    } catch (err) {
      setError('An error occurred while fetching incomplete payments.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [hasPermission]);

  useEffect(() => {
    fetchIncompleteUpgrades();
  }, [fetchIncompleteUpgrades]);

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center py-24 select-none">
        <div className="w-9 h-9 border-4 border-[#2ab391] border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-3 text-sm text-gray-500 font-semibold mt-3">Loading incomplete payments...</span>
      </div>
    );
  }

  // Filtered by date range
  const filteredIncompleteUpgrades = incompleteUpgrades.filter(item => {
    if (!item.created_at) return true;
    const itemDateStr = item.created_at.substring(0, 10);
    if (incompleteFilters.startDate && itemDateStr < incompleteFilters.startDate) return false;
    if (incompleteFilters.endDate && itemDateStr > incompleteFilters.endDate) return false;
    return true;
  });

  const incompleteTotalPages = Math.ceil(filteredIncompleteUpgrades.length / incompletePageSize);
  const safeIncompletePage = Math.min(incompletePage, Math.max(0, incompleteTotalPages - 1));
  const displayedIncompleteList = filteredIncompleteUpgrades.slice(safeIncompletePage * incompletePageSize, (safeIncompletePage + 1) * incompletePageSize);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-left">
      {error && (
        <div className="bg-red-50 text-red-700 border border-red-200/50 p-4 rounded-xl mb-6 flex items-center gap-3 shadow-sm">
          <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      {/* Tab Title & Date Filters */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Incomplete Payments</h2>
        </div>
        
        {/* Date Filter Inputs */}
        <div className="flex flex-wrap items-center gap-3 bg-gray-50 border border-gray-100 p-3 rounded-xl select-none">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Date:</span>
            <DateRangePicker
              startDate={incompleteFilters.startDate}
              endDate={incompleteFilters.endDate}
              onChange={(start, end) => {
                setIncompleteFilters(prev => ({ ...prev, startDate: start, endDate: end }));
                setIncompletePage(0);
              }}
              placeholder="Select Date Range"
            />
          </div>
          {(incompleteFilters.startDate || incompleteFilters.endDate) && (
            <button
              onClick={() => {
                setIncompleteFilters({ startDate: '', endDate: '' });
                setIncompletePage(0);
              }}
              className="px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 border border-red-200/50 rounded-lg transition-colors cursor-pointer"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Info banner */}
      <div className="mb-6 text-xs font-semibold text-gray-600 bg-amber-50/50 border border-amber-200/50 p-4 rounded-xl flex items-center gap-2 select-none">
        <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
        This list shows vendors who clicked 'Upgrade Plan' but did not complete the transaction successfully. Follow up directly using their mobile numbers.
      </div>

      {/* Incomplete Record Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider text-left">
              <th className="p-4 pl-6">Company / Vendor Email</th>
              <th className="p-4">Attempted Plan</th>
              <th className="p-4">Mobile Number</th>
              <th className="p-4">Attempted At</th>
              <th className="p-4 pr-6">Status / Order ID</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-sm text-gray-600">
            {displayedIncompleteList.length === 0 ? (
              <tr>
                <td colSpan="5" className="p-12 text-center text-gray-400 font-semibold">
                  No incomplete upgrades found.
                </td>
              </tr>
            ) : (
              displayedIncompleteList.map(txn => (
                <tr key={txn.id || txn.razorpay_order_id} className="hover:bg-gray-50 transition-all border-b border-gray-100 last:border-0">
                  <td className="p-4 pl-6 text-left">
                    <div className="font-semibold text-gray-800">{txn.company_name}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{txn.vendor_email}</div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-0.5 text-[10px] font-extrabold rounded-md ${txn.plan_name === 'PREMIUM' ? 'bg-violet-100 text-violet-700' :
                      txn.plan_name === 'PROFESSIONAL' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                      {txn.plan_name}
                    </span>
                  </td>
                  <td className="p-4 text-sm font-semibold text-gray-700 text-left">
                    {txn.phone && txn.phone !== 'N/A' ? (
                      <button
                        onClick={() => setRevealedIncompletePhone(prev => prev === (txn.id || txn.razorpay_order_id) ? null : (txn.id || txn.razorpay_order_id))}
                        className="flex items-center gap-1.5 hover:text-indigo-600 transition-colors duration-150 focus:outline-none"
                        title={revealedIncompletePhone === (txn.id || txn.razorpay_order_id) ? "Hide phone number" : "Click to view phone number"}
                      >
                        <span className="font-mono text-gray-600 tracking-wider">
                          {revealedIncompletePhone === (txn.id || txn.razorpay_order_id) ? txn.phone : maskPhoneNumber(txn.phone)}
                        </span>
                        {revealedIncompletePhone === (txn.id || txn.razorpay_order_id) ? (
                          <EyeOff size={14} className="text-gray-400 hover:text-indigo-500" />
                        ) : (
                          <Eye size={14} className="text-gray-400 hover:text-indigo-500" />
                        )}
                      </button>
                    ) : (
                      <span className="text-gray-400 font-normal">N/A</span>
                    )}
                  </td>
                  <td className="p-4 text-xs font-semibold text-gray-700 text-left">
                    {txn.created_at ? new Date(txn.created_at).toLocaleString('en-IN') : 'N/A'}
                  </td>
                  <td className="p-4 pr-6 text-left">
                    <div className="flex flex-col gap-1 items-start">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-extrabold ${txn.status === 'PENDING' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'
                        }`}>
                        {txn.status}
                      </span>
                      <span className="text-[10px] text-gray-400 font-mono select-all">
                        ID: {txn.razorpay_order_id}
                      </span>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between text-xs text-gray-505 font-medium border-t border-gray-100 pt-5 mt-6 select-none bg-gray-50/40">
        <div className="text-gray-500 text-xs font-semibold text-center md:text-left w-full md:w-auto">
          {filteredIncompleteUpgrades.length === 0 ? (
            'No records available'
          ) : (
            `Showing ${safeIncompletePage * incompletePageSize + 1} to ${Math.min((safeIncompletePage + 1) * incompletePageSize, filteredIncompleteUpgrades.length)} of ${filteredIncompleteUpgrades.length} entries`
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full md:w-auto">
          <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
            <span className="text-xs text-gray-555 font-bold uppercase tracking-wider">Show</span>
            <div className="relative">
              <select
                value={incompletePageSize}
                onChange={(e) => {
                  setIncompletePageSize(Number(e.target.value));
                  setIncompletePage(0);
                }}
                className="w-16 bg-white border border-gray-200 rounded-xl pl-3 pr-8 py-1.5 text-xs font-bold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] appearance-none cursor-pointer shadow-xs"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-gray-400">
                <ChevronDown size={12} />
              </span>
            </div>
            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Entries</span>
          </div>

          {incompleteTotalPages > 0 && (
            <div className="flex items-center justify-between sm:justify-start gap-1 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setIncompletePage(p => Math.max(0, p - 1))}
                disabled={safeIncompletePage === 0}
                className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-sm flex-1 sm:flex-initial text-center"
              >
                Previous
              </button>

              <div className="hidden sm:flex items-center gap-1">
                {getPageNumbers(safeIncompletePage, incompleteTotalPages).map((pageNum, idx) => {
                  if (pageNum === 'ellipsis1' || pageNum === 'ellipsis2') {
                    return (
                      <span key={`ell-${idx}`} className="px-2 py-1.5 text-gray-400 font-bold">
                        ...
                      </span>
                    );
                  }
                  const isActive = safeIncompletePage === pageNum;
                  return (
                    <button
                      key={`page-${pageNum}`}
                      type="button"
                      onClick={() => setIncompletePage(pageNum)}
                      className={`min-w-8 h-8 px-2.5 flex items-center justify-center rounded-xl text-xs font-bold border transition-all cursor-pointer ${isActive
                        ? 'bg-[#2ab391] border-[#2ab391] text-white shadow-sm'
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
              </div>

              <span className="sm:hidden text-xs text-gray-500 font-bold px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl shadow-sm min-w-[70px] text-center">
                {safeIncompletePage + 1} / {incompleteTotalPages}
              </span>

              <button
                type="button"
                onClick={() => setIncompletePage(p => Math.min(incompleteTotalPages - 1, p + 1))}
                disabled={safeIncompletePage >= incompleteTotalPages - 1}
                className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-sm flex-1 sm:flex-initial text-center"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IncompleteMembershipsTab;
